/* ============================================================================
 * WIDE — Simulateur : BARÈMES PUBLICS
 * ----------------------------------------------------------------------------
 * Ce fichier part dans le navigateur du visiteur. Il ne contient que les
 * valeurs utilisées par le calcul — aucune provenance, aucune note interne,
 * aucune mention de chiffre provisoire.
 *
 * La provenance de chaque valeur (source, date de relevé, fourchette de
 * plausibilité) vit dans config-sources.js, qui n'est PAS chargé par la page
 * publique : il alimente le back-office et les tests. Ne le référencez jamais
 * depuis index.html.
 *
 * Les deux fichiers doivent porter exactement les mêmes clés. Un test le
 * vérifie à chaque exécution de `node tests.js`.
 * ========================================================================= */

const GLOBAL = {
  notaryFeesRateAncien: 0.078,
  notaryFeesRateNeuf: 0.025,
  agencyFeesRate: 0.03,
  bankFeesRate: 0.0015,
  legalFeesFixed: 1500,
  managementFeesAnnual: 1500,
  sellingCostRate: 0.05,
  furniturePerSqm: 150,
  worksPerSqm: 250,
  fitOutLifespanYears: 9,
  fitOutRenewalRate: 0.6,
  ownerMaintenancePerSqmYear: 8,
  depositMonths: 3,
  agencyFeesRateRental: 0.2,
  rentIndexation: 0.015,
  chargesIndexation: 0.03,
  propertyTaxIndexation: 0.032,
  wideIndexation: 0.02,
  propertyAppreciation: 0.01,
  discountRate: 0.03,
  showResidualValueByDefault: true,
  allowVisitorToToggleResidual: true,
};

const LOCALITIES = [
  {
    id: 'saint-etienne',
    name: 'Saint-Étienne',
    department: '42',
    wideAvailable: true,
    wideSite: 'Saint-Étienne',
    distanceToWideSiteKm: 0,
    travelTimeToWideSite: null,
    rentPerSqmYear: 135,
    rentPerSqmYearMin: 90,
    rentPerSqmYearMax: 200,
    propertyPricePerSqm: 1250,
    propertyPricePerSqmMin: 900,
    propertyPricePerSqmMax: 2500,
    chargesImmobilieresPerSqmMonthly: 4.5,
    servicesOccupantsPerSqmMonthly: 7.5,
    propertyTaxPerSqmYear: 20,
    wideWorkstationPrice: 250,
    wideSharedSurfaceRate: 0.3,
    wideDepositMonths: 1,
  },
  {
    id: 'andrezieux-bouthéon',
    name: 'Andrézieux-Bouthéon',
    department: '42',
    wideAvailable: true,
    wideSite: 'Andrézieux-Bouthéon',
    distanceToWideSiteKm: 0,
    travelTimeToWideSite: null,
    rentPerSqmYear: 150,
    rentPerSqmYearMin: 110,
    rentPerSqmYearMax: 190,
    propertyPricePerSqm: 1200,
    propertyPricePerSqmMin: 900,
    propertyPricePerSqmMax: 2200,
    chargesImmobilieresPerSqmMonthly: 3.5,
    servicesOccupantsPerSqmMonthly: 7.5,
    propertyTaxPerSqmYear: 10,
    wideWorkstationPrice: 250,
    wideSharedSurfaceRate: 0.3,
    wideDepositMonths: 1,
  },
  {
    id: 'chambery',
    name: 'Chambéry',
    department: '73',
    wideAvailable: false,
    wideSite: 'Saint-Étienne',
    distanceToWideSiteKm: 150,
    travelTimeToWideSite: '1h45',
    rentPerSqmYear: 204,
    rentPerSqmYearMin: 92,
    rentPerSqmYearMax: 340,
    propertyPricePerSqm: 2385,
    propertyPricePerSqmMin: 1346,
    propertyPricePerSqmMax: 3016,
    chargesImmobilieresPerSqmMonthly: 4.5,
    servicesOccupantsPerSqmMonthly: 7.5,
    propertyTaxPerSqmYear: 20,
    wideWorkstationPrice: 250,
    wideSharedSurfaceRate: 0.3,
    wideDepositMonths: 1,
  },
  {
    id: 'valence',
    name: 'Valence',
    department: '26',
    wideAvailable: false,
    wideSite: 'Saint-Étienne',
    distanceToWideSiteKm: 122,
    travelTimeToWideSite: '1h20',
    rentPerSqmYear: 150,
    rentPerSqmYearMin: 80,
    rentPerSqmYearMax: 254,
    propertyPricePerSqm: 1547,
    propertyPricePerSqmMin: 667,
    propertyPricePerSqmMax: 2899,
    chargesImmobilieresPerSqmMonthly: 4.5,
    servicesOccupantsPerSqmMonthly: 7.5,
    propertyTaxPerSqmYear: 20,
    wideWorkstationPrice: 250,
    wideSharedSurfaceRate: 0.3,
    wideDepositMonths: 1,
  },
];

const UI = {
  collaboratorsMin: 4,
  collaboratorsMax: 50,
  collaboratorsStep: 4,
  // Horizon COMMUN aux trois options : c'était impossible dans la version
  // précédente, qui projetait la location sur 3/6/9 et l'achat sur 10/15.
  horizonOptions: [3, 6, 9, 10, 15],
  horizonDefault: 3,
  downPaymentRateMin: 0.10,
  downPaymentRateMax: 0.50,
  downPaymentRateDefault: 0.30,
  downPaymentRateStep: 0.01,
  interestRateMin: 2,
  interestRateMax: 7,
  interestRateDefault: 3.5,
  acquisitionRegimeDefault: 'ancien',   // 'ancien' | 'neuf'
  interestRateStep: 0.25,
  // Au-delà, le couple loyer / prix d'acquisition sort du plausible :
  // l'interface affiche le rendement implicite pour le signaler.
  // Saint-Étienne est structurellement un marché à très haut rendement :
  // 134 €/m²/an de loyer pour 1 214 €/m² à l'achat donne 11 %. Une alerte
  // calée sur les moyennes nationales se déclencherait à tort.
  impliedYieldWarnBelow: 0.05,
  impliedYieldWarnAbove: 0.14,
};

/* --- Extraction : le moteur travaille sur des nombres nus -------------- */
function plain(obj) {
  const out = {};
  for (const k in obj) {
    out[k] = obj[k] && typeof obj[k] === 'object' && 'valeur' in obj[k] ? obj[k].valeur : obj[k];
  }
  return out;
}

function resolveConfig(localityId) {
  const loc = LOCALITIES.find((l) => l.id === localityId);
  if (!loc) throw new Error('Localité inconnue : ' + localityId);
  return { global: plain(GLOBAL), locality: plain(loc), ui: UI, raw: { GLOBAL, locality: loc } };
}


function resolveConfig(localityId) {
  const locality = LOCALITIES.find((l) => l.id === localityId);
  if (!locality) throw new Error('Localité inconnue : ' + localityId);
  return { global: GLOBAL, locality, ui: UI };
}

const CONFIG = { GLOBAL, LOCALITIES, UI, resolveConfig };

if (typeof module !== 'undefined' && module.exports) module.exports = CONFIG;
if (typeof window !== 'undefined') window.WIDE_CONFIG = CONFIG;
